// src/pages/info/document-viewer.tsx
import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Download, Copy, Check, Clock, BookOpen, Zap, Settings } from "lucide-react";
import { CURRENCIES } from "@/lib/constants";
import { toast } from "@/components/ui/use-toast";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

interface DocMetadata {
  id: string;
  title: string;
  filename: string;
  bulletPointsFilename: string;
  size: string;
  readingTime: string;
  bulletPointsReadingTime: string;
}

// Component for pre/code blocks with copy button
const PreWithCopy = ({ children, ...props }: any) => {
  const [codeCopied, setCodeCopied] = React.useState(false);

  const getCodeContent = () => {
    if (typeof children === 'string') return children;
    if (children?.props?.children) {
      if (typeof children.props.children === 'string') return children.props.children;
      if (Array.isArray(children.props.children)) {
        return children.props.children.join('');
      }
    }
    return '';
  };

  const handleCopyCode = () => {
    const code = getCodeContent();
    navigator.clipboard.writeText(code);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  return (
    <div className="relative group my-4">
      <button
        onClick={handleCopyCode}
        className="absolute top-2 right-2 p-2 bg-background/90 hover:bg-background border border-border rounded opacity-0 group-hover:opacity-100 transition-opacity z-10"
        aria-label="Copy code"
        type="button"
      >
        {codeCopied ? (
          <Check className="h-4 w-4 text-green-600" />
        ) : (
          <Copy className="h-4 w-4" />
        )}
      </button>
      <pre
        className="bg-slate-100 dark:bg-slate-800 text-foreground p-4 pr-12 rounded-md overflow-x-auto border border-border"
        {...props}
      >
        {children}
      </pre>
    </div>
  );
};

const documentMetadata: Record<string, DocMetadata> = {
  "ai-discovery-guide": {
    id: "ai-discovery-guide",
    title: "AI Discovery Guide",
    filename: "ai-discovery-guide.md",
    bulletPointsFilename: "ai-discovery-guide-bullet-points.md",
    size: "~3,000 words",
    readingTime: "~12 min read",
    bulletPointsReadingTime: "~3 min read",
  },
  "white-label-template": {
    id: "white-label-template",
    title: "AI Discovery White-Label Template",
    filename: "ai-discovery-white-label-template.md",
    bulletPointsFilename: "ai-discovery-white-label-template-bullet-points.md",
    size: "~7,000 words",
    readingTime: "~28 min read",
    bulletPointsReadingTime: "~3 min read",
  },
  "customization-checklist": {
    id: "customization-checklist",
    title: "Agency Customization Checklist",
    filename: "agency-customization-checklist.md",
    bulletPointsFilename: "agency-customization-checklist-bullet-points.md",
    size: "~4,500 words",
    readingTime: "~18 min read",
    bulletPointsReadingTime: "~2 min read",
  },
  "generate-prompt": {
    id: "generate-prompt",
    title: "DIY Generation Prompt",
    filename: "generate-ai-discovery-files-prompt.md",
    bulletPointsFilename: "generate-ai-discovery-files-prompt-bullet-points.md",
    size: "~3,000 words",
    readingTime: "~12 min read",
    bulletPointsReadingTime: "~1 min read",
  },
};

export default function DocumentViewer() {
  const { documentId } = useParams<{ documentId: string }>();
  const location = useLocation();
  const [content, setContent] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Template variables state (for white-label template)
  const [templateVars, setTemplateVars] = useState({
    "CLIENT NAME": "",
    "AGENCY NAME": "",
    "DATE": "",
    "YOUR COMPANY": "",
    "YOUR SERVICE": "",
    "PRICE": "",
    "SETUP PRICE": "",
    "MONTHLY PRICE": "",
    "RUSH FEE": "",
    "AVERAGE VALUE": "",
    "CURRENCY": "$", // Default to dollar
  });

  // Check if we're showing bullet points version
  const isBulletPoints = location.pathname.endsWith("/bullet-points");

  const doc = documentId ? documentMetadata[documentId] : null;

  // Check if this is the white-label template (needs variable replacement)
  const isTemplate = documentId === "white-label-template";

  // Replace variables in content
  const processedContent = useMemo(() => {
    if (!isTemplate || !content) return content;

    let processed = content;

    // First pass: replace currency symbol ($) with selected currency
    const currencySymbol = templateVars["CURRENCY"] || "$";
    // Replace $ that appears before price-related brackets or numbers
    processed = processed.replace(/\$/g, currencySymbol);

    // Second pass: replace all variables (filled and unfilled)
    Object.entries(templateVars).forEach(([key, value]) => {
      // Skip CURRENCY as it's handled separately
      if (key === "CURRENCY") return;

      // Escape special regex characters in key (handles spaces and other chars)
      const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`\\[${escapedKey}\\]`, "g");

      if (value) {
        // Variable is filled in - replace with the value (normal text)
        processed = processed.replace(regex, value);
      } else {
        // Variable is NOT filled in - replace with bold red span using className
        processed = processed.replace(
          regex,
          `<span class="variable-unfilled">[${key}]</span>`
        );
      }
    });

    return processed;
  }, [content, templateVars, isTemplate]);

  // Function to handle variable input changes
  const handleVarChange = (key: string, value: string) => {
    setTemplateVars((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  useEffect(() => {
    if (!doc) {
      setError("Document not found");
      setIsLoading(false);
      return;
    }

    fetchDocument();
  }, [documentId, isBulletPoints]);

  const fetchDocument = async () => {
    if (!doc) return;

    const filename = isBulletPoints ? doc.bulletPointsFilename : doc.filename;

    try {
      setIsLoading(true);
      const response = await fetch(`/docs/${filename}`);
      if (!response.ok) throw new Error("Failed to fetch document");

      const text = await response.text();
      setContent(text);
      setError(null);
    } catch (err) {
      setError("Failed to load document. Please try again.");
      console.error("Error loading document:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!doc) return;

    const filename = isBulletPoints ? doc.bulletPointsFilename : doc.filename;

    try {
      let contentToDownload: string;

      if (isTemplate) {
        // For templates, use processed content with variables replaced
        // Strip HTML tags from processed content for clean markdown download
        contentToDownload = processedContent.replace(/<span[^>]*>(.*?)<\/span>/g, '$1');
      } else {
        // For regular documents, fetch the file
        const response = await fetch(`/docs/${filename}`);
        if (!response.ok) throw new Error("Failed to fetch document");
        contentToDownload = await response.text();
      }

      const blob = new Blob([contentToDownload], { type: "text/markdown" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Download started",
        description: `Downloading ${doc.title}`,
      });
    } catch (error) {
      console.error("Download error:", error);
      toast({
        title: "Download failed",
        description: "Could not download the file. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCopyMarkdown = async () => {
    try {
      // Use processed content if it's a template, otherwise use raw content
      let contentToCopy = isTemplate ? processedContent : content;

      // Strip HTML tags from processed content for clean markdown copy
      if (isTemplate) {
        contentToCopy = contentToCopy.replace(/<span[^>]*>(.*?)<\/span>/g, '$1');
      }

      await navigator.clipboard.writeText(contentToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);

      toast({
        title: "Copied to clipboard",
        description: `${doc?.title} markdown copied successfully`,
      });
    } catch (error) {
      console.error("Copy error:", error);
      toast({
        title: "Copy failed",
        description: "Could not copy markdown. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
        </div>
      </div>
    );
  }

  if (!doc || error) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <Button variant="ghost" asChild className="mb-6">
          <Link to="/documentation">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Documentation
          </Link>
        </Button>
        <Card>
          <CardContent className="py-12 text-center">
            <BookOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-semibold mb-2">
              {error || "Document Not Found"}
            </h2>
            <p className="text-muted-foreground mb-6">
              The document you're looking for doesn't exist or couldn't be loaded.
            </p>
            <Button asChild>
              <Link to="/documentation">View All Documentation</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      {/* CSS for unfilled variables */}
      <style>{`
        .variable-unfilled {
          font-weight: 700;
          color: #dc2626;
        }
      `}</style>

      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <Button variant="ghost" asChild>
          <Link to="/documentation">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Documentation
          </Link>
        </Button>
        <div className="flex gap-2">
          {isBulletPoints ? (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/documentation/${doc.id}`}>
                <BookOpen className="h-4 w-4 mr-2" />
                Full Document
              </Link>
            </Button>
          ) : (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/documentation/${doc.id}/bullet-points`}>
                <Zap className="h-4 w-4 mr-2" />
                Bullet Points
              </Link>
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleDownload}>
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
          <Button variant="outline" size="sm" onClick={handleCopyMarkdown}>
            {copied ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Document Title */}
      <div className="mb-6">
        <h1 className="text-4xl font-bold mb-2">
          {doc.title}
          {isBulletPoints && (
            <span className="text-2xl text-muted-foreground ml-3">
              - Key Takeaways
            </span>
          )}
        </h1>
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            {isBulletPoints ? doc.bulletPointsReadingTime : doc.readingTime}
          </span>
          {!isBulletPoints && (
            <>
              <span>•</span>
              <span>{doc.size}</span>
            </>
          )}
        </div>
      </div>

      {/* Template Variables (only for white-label template) */}
      {isTemplate && (
        <Card className="mb-6">
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="variables" className="border-none">
              <AccordionTrigger className="px-6 hover:no-underline">
                <div className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  <span className="font-semibold">
                    Customize Template Variables
                  </span>
                  <span className="text-sm text-muted-foreground ml-2">
                    (Fill in to personalize the template)
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="currency">Currency</Label>
                    <Select
                      value={templateVars["CURRENCY"]}
                      onValueChange={(value) => handleVarChange("CURRENCY", value)}
                    >
                      <SelectTrigger id="currency">
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map((currency) => (
                          <SelectItem key={currency.symbol} value={currency.symbol}>
                            {currency.symbol} - {currency.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client-name">Client Name</Label>
                    <Input
                      id="client-name"
                      placeholder="e.g., Acme Corporation"
                      value={templateVars["CLIENT NAME"]}
                      onChange={(e) =>
                        handleVarChange("CLIENT NAME", e.target.value)
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="agency-name">Your Agency Name</Label>
                    <Input
                      id="agency-name"
                      placeholder="e.g., Digital Marketing Pro"
                      value={templateVars["AGENCY NAME"]}
                      onChange={(e) =>
                        handleVarChange("AGENCY NAME", e.target.value)
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date">Proposal Date</Label>
                    <Input
                      id="date"
                      placeholder="e.g., December 15, 2024"
                      value={templateVars["DATE"]}
                      onChange={(e) =>
                        handleVarChange("DATE", e.target.value)
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="your-company">Client's Company Name</Label>
                    <Input
                      id="your-company"
                      placeholder="e.g., Acme Corporation"
                      value={templateVars["YOUR COMPANY"]}
                      onChange={(e) =>
                        handleVarChange("YOUR COMPANY", e.target.value)
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="your-service">Client's Service/Product</Label>
                    <Input
                      id="your-service"
                      placeholder="e.g., eco-friendly packaging"
                      value={templateVars["YOUR SERVICE"]}
                      onChange={(e) =>
                        handleVarChange("YOUR SERVICE", e.target.value)
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="price">Base Price</Label>
                    <Input
                      id="price"
                      placeholder={`e.g., ${templateVars["CURRENCY"]}3,500`}
                      value={templateVars["PRICE"]}
                      onChange={(e) => handleVarChange("PRICE", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="setup-price">Setup Price</Label>
                    <Input
                      id="setup-price"
                      placeholder={`e.g., ${templateVars["CURRENCY"]}2,500`}
                      value={templateVars["SETUP PRICE"]}
                      onChange={(e) =>
                        handleVarChange("SETUP PRICE", e.target.value)
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="monthly-price">Monthly Price</Label>
                    <Input
                      id="monthly-price"
                      placeholder={`e.g., ${templateVars["CURRENCY"]}500`}
                      value={templateVars["MONTHLY PRICE"]}
                      onChange={(e) =>
                        handleVarChange("MONTHLY PRICE", e.target.value)
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rush-fee">Rush Fee (optional)</Label>
                    <Input
                      id="rush-fee"
                      placeholder={`e.g., ${templateVars["CURRENCY"]}1,000`}
                      value={templateVars["RUSH FEE"]}
                      onChange={(e) =>
                        handleVarChange("RUSH FEE", e.target.value)
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="average-value">Customer Avg. Value</Label>
                    <Input
                      id="average-value"
                      placeholder={`e.g., ${templateVars["CURRENCY"]}2,500`}
                      value={templateVars["AVERAGE VALUE"]}
                      onChange={(e) =>
                        handleVarChange("AVERAGE VALUE", e.target.value)
                      }
                    />
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-4">
                  💡 Variables in the document will appear in{" "}
                  <span className="variable-unfilled">[BRACKETS]</span>{" "}
                  until you fill them in. Download or copy to get your
                  customized version.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Card>
      )}

      {/* Document Content */}
      <Card>
        <CardContent className="prose prose-slate dark:prose-invert max-w-none pt-6 pb-8">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              // Custom styling for markdown elements
              h1: ({ node, ...props }) => (
                <h1
                  className="text-3xl font-bold mt-8 mb-4 pb-2 border-b border-border"
                  {...props}
                />
              ),
              h2: ({ node, ...props }) => (
                <h2 className="text-2xl font-semibold mt-8 mb-4" {...props} />
              ),
              h3: ({ node, ...props }) => (
                <h3 className="text-xl font-semibold mt-6 mb-3" {...props} />
              ),
              h4: ({ node, ...props }) => (
                <h4 className="text-lg font-semibold mt-4 mb-2" {...props} />
              ),
              p: ({ node, ...props }) => (
                <p className="mb-4 leading-7" {...props} />
              ),
              a: ({ node, ...props }) => (
                <a
                  className="text-primary hover:underline font-medium"
                  target={props.href?.startsWith("http") ? "_blank" : undefined}
                  rel={
                    props.href?.startsWith("http")
                      ? "noopener noreferrer"
                      : undefined
                  }
                  {...props}
                />
              ),
              ul: ({ node, ...props }) => (
                <ul className="list-disc list-inside mb-4 space-y-2" {...props} />
              ),
              ol: ({ node, ...props }) => (
                <ol
                  className="list-decimal list-inside mb-4 space-y-2"
                  {...props}
                />
              ),
              li: ({ node, ...props }) => <li className="ml-4" {...props} />,
              blockquote: ({ node, ...props }) => (
                <blockquote
                  className="border-l-4 border-primary pl-4 py-2 my-4 italic text-muted-foreground bg-muted/30 rounded-r"
                  {...props}
                />
              ),
              code: ({ node, inline, ...props }: any) =>
                inline ? (
                  <code
                    className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono text-foreground"
                    {...props}
                  />
                ) : (
                  <code className="block text-foreground" {...props} />
                ),
              pre: ({ node, ...props }) => <PreWithCopy {...props} />,
              hr: ({ node, ...props }) => (
                <hr className="my-8 border-border" {...props} />
              ),
              table: ({ node, ...props }) => (
                <div className="overflow-x-auto my-4">
                  <table
                    className="min-w-full divide-y divide-border"
                    {...props}
                  />
                </div>
              ),
              th: ({ node, ...props }) => (
                <th
                  className="px-4 py-2 text-left font-semibold bg-muted"
                  {...props}
                />
              ),
              td: ({ node, ...props }) => (
                <td className="px-4 py-2 border-t border-border" {...props} />
              ),
              strong: ({ node, ...props }) => (
                <strong className="font-semibold" {...props} />
              ),
              em: ({ node, ...props }) => <em className="italic" {...props} />,
            }}
          >
            {processedContent}
          </ReactMarkdown>
        </CardContent>
      </Card>

      {/* Back to top button */}
      <div className="mt-8 text-center">
        <Button
          variant="outline"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          Back to Top
        </Button>
      </div>
    </div>
  );
}
